'use client';

import { CONTENT_EVENTS } from '@repo/analytics';
import type { IRateMovieV1Body } from '@repo/sdk';
import { trackEvent } from '@repo/web-chrome/lib/analytics';
import { Minus, Plus } from 'lucide-react';
import type React from 'react';
import type { ITrackingStrings } from './tracking-strings';

interface IWatchCountStepperProps {
  collectionId: number;
  count: number;
  pending: boolean;
  strings: ITrackingStrings;
  save: (update: IRateMovieV1Body) => Promise<void>;
}

const STEP_BUTTON_CLASS =
  'flex size-7 items-center justify-center rounded-full border border-border transition outline-none hover:bg-muted focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50';

export function WatchCountStepper({
  collectionId,
  count,
  pending,
  strings,
  save,
}: IWatchCountStepperProps): React.JSX.Element {
  function increase(): void {
    const next = count + 1;
    trackEvent(CONTENT_EVENTS.movieRewatched, { collectionId, watchCount: next });
    void save({ watchCount: next });
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-muted-foreground">{strings.timesWatched}</span>
      <button
        type="button"
        aria-label={strings.decreaseWatchCount}
        disabled={pending || count <= 1}
        onClick={() => void save({ watchCount: count - 1 })}
        className={STEP_BUTTON_CLASS}
      >
        <Minus className="size-4" aria-hidden />
      </button>
      <span aria-live="polite" className="w-8 text-center text-sm font-medium tabular-nums">
        {count}
      </span>
      <button
        type="button"
        aria-label={strings.increaseWatchCount}
        disabled={pending}
        onClick={increase}
        className={STEP_BUTTON_CLASS}
      >
        <Plus className="size-4" aria-hidden />
      </button>
    </div>
  );
}
